export type CancellationOfferContext = {
    plan: string;
    isFreeOrTrial: boolean;
    isCreditBased: boolean;
    hasAcceptedCancelOffer: boolean;
    selectedDowngradePlan: string;
    expiredTrial: boolean;
};

export type CancellationOfferVariant = "credits" | "downgrade" | "externalLink";

export type CancellationOfferDefinition = {
    id: string;
    variant: CancellationOfferVariant;
    heading: string;
    description: string;
    ctaText: string;
    icon: string;
    eligibility: Array<(ctx: CancellationOfferContext) => boolean>;
    variantConfig: {
        creditAmount?: number;
        grantName?: string;
        targetPlan?: string;
        openAction?: string;
    };
};

// Eligibility predicates

const isPaidPlan = (ctx: CancellationOfferContext) => !ctx.isFreeOrTrial;

const isCreditBased = (ctx: CancellationOfferContext) => ctx.isCreditBased;

const hasNotAcceptedOffer = (ctx: CancellationOfferContext) => !ctx.hasAcceptedCancelOffer;

const isNotExpiredTrial = (ctx: CancellationOfferContext) => !ctx.expiredTrial;

const planIs =
    (...plans: string[]) =>
    (ctx: CancellationOfferContext) =>
        plans.includes(ctx.plan);

const planIsNot =
    (...plans: string[]) =>
    (ctx: CancellationOfferContext) =>
        !plans.includes(ctx.plan);

const downgradePlanIsNot =
    (...plans: string[]) =>
    (ctx: CancellationOfferContext) =>
        !plans.includes(ctx.selectedDowngradePlan);

// Cancellation offer definitions
// ! Order matters - the first eligible offer will be the one shown to the user, so more valuable offers should be higher in the list
export const CANCELLATION_OFFERS: CancellationOfferDefinition[] = [
    {
        id: "onboardingCall",
        variant: "externalLink",
        heading: "Before you go, would you like a free onboarding call?",
        description:
            "We want to help you get the most out of your plan and get you set up, or if you're set on cancelling, hear your feedback.",
        ctaText: "Book Onboarding Call",
        icon: "far fa-video",
        eligibility: [isPaidPlan, isNotExpiredTrial, planIs("core", "basic")],
        variantConfig: { openAction: "openOnboardingCalendly" }
    },
    {
        id: "coreDowngrade",
        variant: "downgrade",
        heading: "Instead of canceling, would you like to switch to the Core Plan?",
        description: "Keep access to core features at a lower price.",
        ctaText: "Downgrade to the Core Plan",
        icon: "far fa-arrow-up-arrow-down",
        eligibility: [
            downgradePlanIsNot("basic", "core"),
            isNotExpiredTrial,
            planIsNot("core", "trial", "basic")
        ],
        variantConfig: { targetPlan: "core" }
    },
    {
        id: "roboflowCredits",
        variant: "credits",
        heading: "Instead of canceling, would you like one of these offers?",
        description: "We'll add free credits to your account.",
        ctaText: "Select Offer",
        icon: "far fa-gift",
        eligibility: [isPaidPlan, isCreditBased, hasNotAcceptedOffer],
        variantConfig: { creditAmount: 20, grantName: "Cancellation Offer Promotional Credits" }
    }
];

export function getEligibleCancellationOffer(
    ctx: CancellationOfferContext
): CancellationOfferDefinition | null {
    return (
        CANCELLATION_OFFERS.find((offer) => offer.eligibility.every((check) => check(ctx))) ?? null
    );
}

export function getCancellationOfferById(id: string): CancellationOfferDefinition | null {
    return CANCELLATION_OFFERS.find((offer) => offer.id === id) ?? null;
}
